/**
 * Whom an AI call campaign may ring: the source, its filters, and the two
 * choices every AI call carries (mobiles, existing clients).
 *
 * Two sources and two units, as on the marketing audience: the brain list
 * counts DOMAINS, the register counts COMPANIES. The register's filters are the
 * marketing form itself (`RegisterFilterForm`), on the whole audience for the
 * register and on the company behind each domain for the brain, so the same
 * screenful of filters means the same companies in both tools.
 *
 * The number always comes from the register, whatever the source: the opening
 * and the answer to "where did you get my number" both name it.
 *
 * An alert campaign (ring R9) never rings existing clients: the switch is shown
 * off and locked, and the server refuses such an audience anyway.
 */
import { useEffect, useState } from "react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  PHONE_KINDS,
  type BrainAudienceFilter,
  type CampaignAudience,
  type CampaignPhoneKind,
  type CampaignSource,
} from "../../../../lib/aiCampaigns";
import {
  applyPatch,
  paramsToAudience,
  registerAudience,
  type AudienceFilter,
  type AudienceParams,
} from "../../../../lib/audienceFilter";
import { getRegisterCoverage, type RegisterCoverage } from "../../../../lib/marketing";
import { PillTabs, SelectInput } from "../../../ui";
import { RegisterFilterForm } from "../../marketing/panels/RegisterFilterForm";
import { Field, Panel } from "../../marketing/shared";
import { ToggleRow } from "../../settings/panel/fields";
import { BrainAudienceFields } from "./BrainAudienceFields";

/** The register filter an audience carries, without the campaign's own keys. */
function companyParams(audience: CampaignAudience): AudienceParams {
  if (audience.source === "brain") return audience.filter.company ?? {};
  const { includeClients: _clients, phoneKind: _kind, ...params } = audience.filter;
  return params;
}

/**
 * The audience for a source, carrying the company filter and the two shared
 * choices across. A choice left at its default is an absent key.
 */
function build(
  source: CampaignSource,
  brain: BrainAudienceFilter,
  params: AudienceParams,
  shared: { includeClients: boolean; phoneKind: CampaignPhoneKind },
): CampaignAudience {
  const own = {
    ...(shared.includeClients ? { includeClients: true } : {}),
    ...(shared.phoneKind === "landline" ? { phoneKind: "landline" as const } : {}),
  };
  if (source === "register") return { source, filter: { ...params, ...own } };
  const { company: _old, includeClients: _c, phoneKind: _k, ...rest } = brain;
  const hasCompany = Object.keys(params).length > 0;
  return { source, filter: { ...rest, ...own, ...(hasCompany ? { company: params } : {}) } };
}

export function CampaignAudienceCard({
  audience,
  onChange,
  disabled,
  resetKey,
  alert,
}: {
  audience: CampaignAudience;
  onChange: (next: CampaignAudience) => void;
  disabled?: boolean;
  /** Changes when an audience is loaded from outside (a preset, another campaign). */
  resetKey: string;
  /** An alert campaign: existing clients stay out. */
  alert?: boolean;
}) {
  const t = useT();
  const [coverage, setCoverage] = useState<RegisterCoverage | null>(null);
  const [company, setCompany] = useState<AudienceFilter>(() => paramsToAudience(companyParams(audience)));

  useEffect(() => {
    getRegisterCoverage()
      .then(setCoverage)
      .catch(() => {
        /* the form works without the coverage badges */
      });
  }, []);

  // A preset or a loaded campaign replaces the filter from outside: reseed the form.
  useEffect(() => {
    setCompany(paramsToAudience(companyParams(audience)));
    // Only an outside load reseeds; every edit below already keeps the two in step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const includeClients = !alert && audience.filter.includeClients === true;
  const phoneKind: CampaignPhoneKind = audience.filter.phoneKind ?? "any";

  const brainFilter: BrainAudienceFilter = audience.source === "brain" ? audience.filter : {};
  const params = registerAudience(company) as AudienceParams;

  function setSource(source: CampaignSource) {
    if (source === audience.source) return;
    onChange(build(source, brainFilter, params, { includeClients, phoneKind }));
  }

  function patchCompany(p: Partial<AudienceFilter>) {
    const next = applyPatch(company, p);
    if (next === company) return;
    setCompany(next);
    onChange(build(audience.source, brainFilter, registerAudience(next) as AudienceParams, { includeClients, phoneKind }));
  }

  const sourceTabs = (["brain", "register"] as const).map((value) => ({
    value,
    label: t(`aiCalls.campaigns.source.${value}`),
  }));

  return (
    <Panel title={t("aiCalls.campaigns.audience.title")} description={t("aiCalls.campaigns.audience.intro")}>
      <div className="space-y-5">
        <div>
          <PillTabs tabs={sourceTabs} value={audience.source} onChange={setSource} small />
          <p className="crm-sub mt-2 text-xs">{t(`aiCalls.campaigns.audience.sourceHint.${audience.source}`)}</p>
        </div>

        {audience.source === "brain" ? (
          <BrainAudienceFields
            filter={brainFilter}
            onChange={(next) => onChange(build("brain", next, params, { includeClients, phoneKind }))}
            disabled={disabled}
          />
        ) : null}

        <div>
          <p className="mb-3 text-[13px] font-[600]">
            {t(audience.source === "brain" ? "aiCalls.campaigns.audience.companyFilters" : "aiCalls.campaigns.audience.registerFilters")}
          </p>
          <RegisterFilterForm filter={company} onChange={patchCompany} disabled={disabled} resetKey={resetKey} coverage={coverage} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("aiCalls.campaigns.audience.phoneKind")} hint={t("aiCalls.campaigns.audience.phoneKindHint")}>
            <SelectInput
              value={phoneKind}
              onChange={(v) =>
                onChange(build(audience.source, brainFilter, params, { includeClients, phoneKind: v as CampaignPhoneKind }))
              }
              label={t("aiCalls.campaigns.audience.phoneKind")}
              disabled={disabled}
            >
              {PHONE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`aiCalls.campaigns.audience.phoneKinds.${kind}`)}
                </option>
              ))}
            </SelectInput>
          </Field>
          <div className="flex items-end">
            <ToggleRow
              label={t("aiCalls.campaigns.audience.includeClients")}
              hint={t(alert ? "aiCalls.campaigns.audience.alertClients" : "aiCalls.campaigns.audience.includeClientsHint")}
              checked={includeClients}
              disabled={disabled || alert}
              onChange={(on) => onChange(build(audience.source, brainFilter, params, { includeClients: on, phoneKind }))}
            />
          </div>
        </div>
      </div>
    </Panel>
  );
}
